import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "./Button";
import "./AsyncStates.css";

interface ErrorBoundaryFallbackProps {
  error: Error;
  label?: string;
  /** Widget-level boundaries render a tighter, inline fallback instead of
   * the full-page centered one so a crashing card/panel doesn't force a
   * huge empty block onto the surrounding layout. */
  compact?: boolean;
  onReset: () => void;
}

/** Friendly, on-brand crash fallback — same visual language as
 * `ErrorState`/`EmptyState` (`.async-state`), so a caught render error looks
 * like the rest of the app's error handling, not a stack trace. Never shows
 * anything beyond `error.message`: no stack, no component tree, since either
 * could echo request/response data a caller passed in (charter O-1 — never
 * log/display secrets or workload data). */
function ErrorBoundaryFallback({ error, label, compact, onReset }: ErrorBoundaryFallbackProps) {
  const title = label ? `${label} hit a problem` : "Something went wrong";
  return (
    <div
      className={["async-state", "async-state--error", compact ? "async-state--compact" : ""]
        .filter(Boolean)
        .join(" ")}
      role="alert"
    >
      <p className="async-state__title">{title}</p>
      <p className="async-state__text">{error.message || "An unexpected error stopped this from rendering."}</p>
      <div className="async-state__actions">
        <Button variant="secondary" onClick={onReset}>
          Try again
        </Button>
        <Button variant="primary" onClick={() => window.location.reload()}>
          Reload page
        </Button>
      </div>
    </div>
  );
}

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Short noun phrase for what crashed, shown in the default fallback's
   * heading — e.g. "This page", "The deployments table". Omit for a generic
   * "Something went wrong". */
  label?: string;
  /** Widget-level styling (see ErrorBoundaryFallback). */
  compact?: boolean;
  /** Escape hatch for a caller that wants a fully custom fallback instead of
   * the shared one. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
  /** Called once per catch — the seam for telemetry later. Never pass
   * anything from here into a log line that could carry secrets/workload
   * data (O-1); today this only exists so tests can assert a boundary
   * actually caught something. */
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Catches render/lifecycle errors thrown anywhere beneath it (React error
 * boundaries only catch these — not errors in event handlers, async
 * callbacks, or the error boundary itself) and renders a friendly fallback
 * instead of unmounting the whole app into a white screen. `App.tsx` mounts
 * one of these around the routed page content, re-keyed per route, so one
 * crashing page doesn't take the nav chrome (`AppShell`) down with it;
 * `main.tsx` mounts a second one around the entire app as a last resort for
 * a crash in a provider above the router. This class also doubles as the
 * smaller, per-widget boundary (`compact`) for any view that renders several
 * independent panels and doesn't want one to take the others down.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    this.props.onError?.(error, info);
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(error, this.reset);
    return (
      <ErrorBoundaryFallback error={error} label={this.props.label} compact={this.props.compact} onReset={this.reset} />
    );
  }
}
